import { useTranslation } from 'react-i18next';
import type { TimedFeeVersion } from '@pokerroom/types';

/** Inspect the existing template response without resolving or changing prices. */
export function TimedFeeSummary({
  templateId,
  version,
}: {
  templateId: string;
  version?: TimedFeeVersion | null;
}) {
  const { t } = useTranslation('tournament');
  if (!version)
    return <p className="mt-3 text-sm text-base-content/70">{t('feeSummary.legacy')}</p>;

  const { table } = version;
  const facts: Array<[string, string]> = [
    [t('feeSummary.versionId'), version.id],
    [t('feeSummary.templateId'), templateId],
    [t('feeSummary.principal'), table.principalAmount],
    [t('feeSummary.generalLevelId'), table.generalMemberLevelId],
    [t('timedFees.original'), table.originalVenueFeeAmount],
    [t('timedFees.generalReservation'), table.generalReservationVenueFeeAmount],
    [t('timedFees.afterBreak'), table.afterBreakVenueFeeAmount],
    [t('timedFees.reentry'), table.repeatVenueFees.reentry],
    [t('timedFees.rebuy'), table.repeatVenueFees.rebuy],
    [t('timedFees.addon'), table.repeatVenueFees.addon],
  ];
  const fact = (label: string, value: string) => (
    <div key={label} className="min-w-0">
      <dt className="text-xs text-base-content/70">{label}</dt>
      <dd className="font-medium tabular-nums [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );

  return (
    <details className="mt-3 border border-base-300 p-3 text-sm">
      <summary className="cursor-pointer py-2 font-medium">{t('feeSummary.title')}</summary>
      <p className="my-2 text-base-content/70">{t('feeSummary.source')}</p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {facts.map(([label, value]) => fact(label, value))}
      </dl>
      <h3 className="mt-4 mb-2 font-medium">{t('feeSummary.memberPrices')}</h3>
      {table.memberVenuePrices.length === 0 ? (
        <p className="text-base-content/70">{t('feeSummary.noMemberPrices')}</p>
      ) : (
        <ul className="space-y-3">
          {table.memberVenuePrices.map((price) => (
            <li key={price.memberLevelId} className="border-t border-base-300 pt-2">
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {fact(t('feeSummary.memberLevelId'), price.memberLevelId)}
                {fact(t('timedFees.ordinaryAmount'), price.ordinaryAmount)}
                {fact(t('timedFees.reservationAmount'), price.reservationAmount)}
              </dl>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
